// =========================================================================
// HelpyDoc · Puerta de acceso a las pantallas de administración
// =========================================================================
// Deja pasar solo a quien lleva el claim `role` = admin | superadmin en su ID
// token. Es una puerta de INTERFAZ: evita enseñar el panel a quien no le toca
// y da un mensaje claro en vez de una pantalla rota.
//
// La seguridad de verdad NO está aquí. Alguien que manipule el navegador puede
// forzar que se pinte el panel, y no conseguirá nada: `storage.rules` comprueba
// el mismo claim del lado del servidor y rechaza toda escritura sin él. Este
// componente nunca debe ser la única defensa de nada.
// =========================================================================
import { useEffect, useState } from 'react';
import { ChevronLeft, ShieldOff } from 'lucide-react';
import { rolDelUsuario, puedeAdministrarContenido, type RolUsuario } from '@/lib/admin-rol';
import { SkeletonDashboard } from '@/components/Skeleton';

interface AdminGateProps {
  children: React.ReactNode;
  onBack: () => void;
  /** Exigir el nivel de dirección general (superadmin) en vez de admin. */
  soloSuperadmin?: boolean;
}

export function AdminGate({ children, onBack, soloSuperadmin = false }: AdminGateProps) {
  // `undefined` = aún comprobando; `null` = comprobado y sin rol.
  const [rol, setRol] = useState<RolUsuario | undefined>(undefined);

  useEffect(() => {
    let vivo = true;
    rolDelUsuario().then(r => { if (vivo) setRol(r); });
    return () => { vivo = false; };
  }, []);

  if (rol === undefined) return <SkeletonDashboard />;

  const permitido = soloSuperadmin ? rol === 'superadmin' : puedeAdministrarContenido(rol);
  if (permitido) return <>{children}</>;

  return (
    <div className="ui-modern flex flex-col h-full bg-background">
      <header className="flex-shrink-0 flex items-center gap-3 px-4 py-4 border-b border-border">
        <button type="button" onClick={onBack} aria-label="Volver"
          className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-foreground">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-bold tracking-tight">Acceso restringido</h1>
      </header>
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-3">
        <ShieldOff className="w-10 h-10 text-muted-foreground/60" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          Esta sección es solo para administración de HelpyDoc.
        </p>
        <p className="text-xs text-muted-foreground">
          Si acaban de darte acceso, cierra sesión y vuelve a entrar: el permiso
          viaja en la sesión y la actual todavía no lo lleva.
        </p>
      </div>
    </div>
  );
}
